/**
 * apps/battle/client/src/ClusterMap.tsx — SVG node-graph interactive game map.
 *
 * Redesigned from a plain CSS-grid into a polished hero's-journey adventure
 * map with two world regions, directional prereq edges, and tier-coded portal
 * arcs. Node and portal visual states are derived entirely from /api/map data.
 *
 * Portal battles: available portals are visually selectable; clicking one
 * shows a "content coming soon" panel. No server endpoint for portal battles
 * exists yet — this UI hook is wired and ready; connect it when the backend
 * endpoint lands (see PORTAL_BATTLE_HOOK below).
 *
 * Data contract: imports MapResp / ClusterState / PortalState from api.ts —
 * unchanged. No new server routes created.
 */

import { useState, useCallback, useId, memo } from 'react';
import type { ClusterState, MapResp, PortalState } from './api.js';
import {
  LAYOUT,
  VIEW_W,
  VIEW_H,
  NODE_R,
  toSVG,
  PREREQ_EDGES,
  tierColor,
  tierStrokeWidth,
  tierDashArray,
} from './mapLayout.js';

// ─────────────────────────────────────────────────────────────────────────────
// PORTAL_BATTLE_HOOK
// When the backend lands a portal-battle endpoint (e.g. GET /api/battle/portal?key=X-Y),
// wire it here: replace the `setPortalModal` call in handlePortalClick with a
// call to the real battle launcher. The modal and all UI surrounding it can be
// repurposed as a confirmation sheet before launching.
// ─────────────────────────────────────────────────────────────────────────────

interface ClusterMapProps {
  mapData: MapResp;
  onSelectCluster: (cluster: ClusterState) => void;
  onSelectPortal?: (portal: PortalState) => void;
  /** Phase 4.2 M9-patch11 — read-only Concept Web mode: hover/tooltips stay,
   *  but clicking on a cluster or portal does nothing (no navigation, no modal). */
  readOnly?: boolean;
}

// ── Geometry helpers ─────────────────────────────────────────────────────────

/** Cubic-bezier control points for a portal arc between two SVG coords. */
function portalArcPath(
  x1: number, y1: number,
  x2: number, y2: number,
): string {
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.sqrt(dx * dx + dy * dy);
  // Perpendicular offset — scales with distance so short arcs stay subtle
  const offset = Math.min(len * 0.35, 120);
  // Offset perpendicular to the chord (rotate 90°)
  const nx = -dy / len;
  const ny =  dx / len;
  const cx = mx + nx * offset;
  const cy = my + ny * offset;
  return `M ${x1} ${y1} Q ${cx} ${cy} ${x2} ${y2}`;
}

/** Straight path for prereq edges. */
function prereqLinePath(
  x1: number, y1: number,
  x2: number, y2: number,
): { d: string; arrowX: number; arrowY: number; arrowAngle: number } {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.sqrt(dx * dx + dy * dy);
  // Shorten line so it stops at node border
  const ux = dx / len;
  const uy = dy / len;
  const ex = x2 - ux * (NODE_R + 4);
  const ey = y2 - uy * (NODE_R + 4);
  const sx = x1 + ux * (NODE_R + 4);
  const sy = y1 + uy * (NODE_R + 4);
  const angle = Math.atan2(uy, ux) * (180 / Math.PI);
  return { d: `M ${sx} ${sy} L ${ex} ${ey}`, arrowX: ex, arrowY: ey, arrowAngle: angle };
}

// ── Progress ring ─────────────────────────────────────────────────────────────

interface ProgressRingProps {
  cx: number;
  cy: number;
  pct: number; // 0..1
  color: string;
  r?: number;
}

function ProgressRing({ cx, cy, pct, color, r = NODE_R + 6 }: ProgressRingProps) {
  const circumference = 2 * Math.PI * r;
  const dash = circumference * Math.min(1, Math.max(0, pct));
  return (
    <circle
      cx={cx}
      cy={cy}
      r={r}
      fill="none"
      stroke={color}
      strokeWidth={3}
      strokeDasharray={`${dash} ${circumference - dash}`}
      strokeDashoffset={circumference * 0.25} // start at top
      strokeLinecap="round"
      opacity={0.7}
    />
  );
}

// ── Tooltip state ─────────────────────────────────────────────────────────────

type TooltipData =
  | { kind: 'cluster'; cluster: ClusterState; x: number; y: number }
  | { kind: 'portal'; portal: PortalState; x: number; y: number };

// ── Portal "content coming soon" modal ────────────────────────────────────────

interface PortalModalProps {
  portal: PortalState;
  onClose: () => void;
  onLaunch?: () => void;
}

function PortalModal({ portal, onClose, onLaunch }: PortalModalProps) {
  const tierLabel = portal.tier === 12 ? 'Permanent' : portal.tier === 9 ? 'Conditional' : 'Path';
  const tierColorStr = portal.tier === 12 ? 'var(--violet)' : portal.tier === 9 ? 'var(--warning)' : 'var(--accent-light)';
  // FIX D: parse endpoint cluster ids from portalKey (e.g. 'A-N' -> 'A', 'N')
  const endpointA = portal.endpointA || portal.portalKey.split('-')[0] || '';
  const endpointB = portal.endpointB || portal.portalKey.split('-')[1] || '';
  const aCleared = portal.aCleared ?? false;
  const bCleared = portal.bCleared ?? false;
  return (
    <div className="portal-modal-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label={`Portal ${portal.portalKey}`}>
      <div className="portal-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="portal-modal-header" style={{ borderColor: tierColorStr }}>
          <span className="portal-modal-icon" style={{ color: tierColorStr }}>⬡</span>
          <div>
            <div className="portal-modal-title">Portal {endpointA}–{endpointB}</div>
            <div className="portal-modal-subtitle" style={{ color: tierColorStr }}>
              {tierLabel} · {portal.tier}-mark
            </div>
          </div>
          <button className="portal-modal-close btn btn-secondary" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="portal-modal-body">
          {/* FIX D: true unlock condition with live per-endpoint status */}
          <div style={{ marginBottom: '0.875rem' }}>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.375rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Unlock condition
            </div>
            <div style={{ fontSize: '0.875rem', color: 'var(--text)', marginBottom: '0.5rem' }}>
              Clear both Cluster {endpointA} and Cluster {endpointB}.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <div style={{ fontSize: '0.875rem', color: aCleared ? 'var(--success)' : 'var(--text-muted)' }}>
                {aCleared ? '✓' : '✗'} Cluster {endpointA} — {aCleared ? 'cleared' : 'not yet'}
              </div>
              <div style={{ fontSize: '0.875rem', color: bCleared ? 'var(--success)' : 'var(--text-muted)' }}>
                {bCleared ? '✓' : '✗'} Cluster {endpointB} — {bCleared ? 'cleared' : 'not yet'}
              </div>
            </div>
          </div>
          {/* FIX D: reward info */}
          <div style={{ fontSize: '0.875rem', color: 'var(--accent-light)', marginBottom: '0.875rem' }}>
            Reward: +30 XP bonus when deployed.
          </div>
          {/* M9-patch3: cluster-style N/M progress (replaces "Deployed N times"). */}
          <div className="portal-modal-deploys">
            {portal.total > 0
              ? (portal.solved >= portal.total
                  ? `✓ Completely cleared (${portal.solved}/${portal.total}) — retry to farm XP`
                  : `${portal.solved}/${portal.total} cleared`)
              : 'No portal questions yet'}
          </div>
          {portal.available && onLaunch && (
            <div className="portal-modal-coming">
              <button
                className="btn btn-primary w-full"
                onClick={onLaunch}
                data-testid={`btn-launch-portal-${portal.portalKey}`}
              >
                Enter portal battle →
              </button>
            </div>
          )}
        </div>
        <div className="portal-modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── Tooltip overlay (SVG foreignObject) ──────────────────────────────────────

interface TooltipProps {
  tip: TooltipData;
  svgW: number;
  svgH: number;
}

function Tooltip({ tip, svgW, svgH }: TooltipProps) {
  const TW = 200; // tooltip width in SVG units
  const TH = tip.kind === 'cluster' ? 110 : 90;
  // Clamp so tooltip stays inside viewBox
  const tx = Math.min(tip.x + 12, svgW - TW - 4);
  const ty = Math.min(tip.y - TH / 2, svgH - TH - 4);

  if (tip.kind === 'cluster') {
    const c = tip.cluster;
    const stateLabel = c.locked ? 'Locked'
      : c.clearedLatched || c.stageCleared ? 'Cleared'
      : c.touched ? 'Active'
      : 'Available';
    const lockNote = c.locked
      ? `Requires: ${c.prerequisites.join(', ')}`
      : null;
    return (
      <foreignObject x={tx} y={ty} width={TW} height={TH} style={{ pointerEvents: 'none' }}>
        <div className="map-tooltip">
          <div className="map-tooltip-title">{c.clusterId} · {c.fullName}</div>
          <div className="map-tooltip-state">{stateLabel}</div>
          {lockNote && <div className="map-tooltip-lock">{lockNote}</div>}
          {c.conceptTotal > 0 && (
            <div className="map-tooltip-prog">
              {/* FIX 3: show distinct concept questions cleared/total in tooltip */}
              {c.conceptCleared}/{c.conceptTotal} questions · {Math.round(c.conceptPct * 100)}%
            </div>
          )}
        </div>
      </foreignObject>
    );
  }

  // Portal tooltip — FIX D: remove pass-bar nonsense, show live endpoint status
  const p = tip.portal;
  const tierLabel = p.tier === 12 ? 'Permanent (12m)' : p.tier === 9 ? 'Conditional (9m)' : 'Path (6m)';
  const pEndA = p.endpointA || p.portalKey.split('-')[0] || '';
  const pEndB = p.endpointB || p.portalKey.split('-')[1] || '';
  return (
    <foreignObject x={tx} y={ty} width={TW} height={TH} style={{ pointerEvents: 'none' }}>
      <div className="map-tooltip">
        <div className="map-tooltip-title">Portal {p.portalKey}</div>
        <div className="map-tooltip-state">{tierLabel}</div>
        <div className="map-tooltip-prog">
          {(p.aCleared ?? false) ? '✓' : '✗'} {pEndA} · {(p.bCleared ?? false) ? '✓' : '✗'} {pEndB}
        </div>
        <div className="map-tooltip-state" style={{ color: p.available ? 'var(--success)' : 'var(--text-muted)' }}>
          {p.available ? '✓ Available' : 'Not yet available'}
        </div>
      </div>
    </foreignObject>
  );
}

// ── ClusterNode (SVG group) ───────────────────────────────────────────────────

interface ClusterNodeProps {
  cluster: ClusterState;
  cx: number;
  cy: number;
  onSelect: (cluster: ClusterState) => void;
  onTooltipEnter: (cluster: ClusterState, x: number, y: number) => void;
  onTooltipLeave: () => void;
  dimmed: boolean;
  /** M9-patch11 — disables click handler entirely; hover/tooltip still work. */
  readOnly?: boolean;
}

// FIX 1: wrap in React.memo so hovering one node doesn't re-render siblings.
// Props signature uses stable parent callbacks (onSelect/onTooltipEnter accept the cluster
// object) so no inline arrow is needed at the call site — defeating memo is avoided.
const ClusterNode = memo(function ClusterNode({
  cluster, cx, cy, onSelect, onTooltipEnter, onTooltipLeave, dimmed, readOnly,
}: ClusterNodeProps) {
  const {
    clusterId, fullName, locked, stageCleared, clearedLatched, touched,
    // FIX 3: use distinct-concept metric for ring + count text
    conceptCleared, conceptTotal, conceptPct,
  } = cluster;

  const isCleared = stageCleared || clearedLatched;

  // Determine fill/stroke based on state
  const fillColor = locked ? 'var(--surface-2)' : isCleared ? 'rgba(34,197,94,0.12)' : touched ? 'rgba(99,102,241,0.18)' : 'var(--surface-2)';
  const strokeColor = locked ? 'var(--border)' : isCleared ? 'var(--success)' : touched ? 'var(--accent)' : 'var(--border)';
  const strokeWidth = locked ? 1.5 : isCleared || touched ? 2.5 : 1.5;

  // M9-patch5: boss layer removed. Only lock badge remains.
  const badges: string[] = [];
  if (locked) badges.push('🔒');

  const labelLines = splitLabel(fullName);

  const opacity = dimmed ? 0.4 : locked ? 0.55 : 1;

  // FIX 1: stable per-node event handlers — call parent with cluster, no inline arrows
  const handleMouseEnter = useCallback(() => {
    onTooltipEnter(cluster, cx + NODE_R + 8, cy);
  }, [onTooltipEnter, cluster, cx, cy]);

  return (
    // FIX C: Outer <g> is the event-receiver and stays at scale(1) so hover bounds are FIXED.
    // The CSS scale(1.08) hover transform is applied only to the inner .node-visual <g>,
    // which prevents the hit-area from moving under the cursor and causing mouseleave/mouseenter thrash.
    <g
      className="map-cluster-node"
      transform={`translate(${cx},${cy})`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={onTooltipLeave}
      opacity={opacity}
      data-testid={`cluster-node-${clusterId}`}
    >
      {/* FIX C: Fixed invisible hit-rect — stable hit area regardless of inner scale transform */}
      <circle
        cx={0} cy={0}
        r={NODE_R + 10}
        fill="transparent"
        stroke="none"
        style={{ cursor: readOnly ? 'default' : (locked ? 'not-allowed' : 'pointer') }}
        onClick={readOnly || locked ? undefined : () => onSelect(cluster)}
        role={readOnly || locked ? undefined : 'button'}
        aria-label={locked ? `${fullName} — locked` : fullName}
        aria-disabled={locked}
      />
      {/* FIX C: Inner group receives the CSS scale hover transform — does NOT affect outer hit bounds */}
      <g
        className={`node-visual${locked ? ' map-cluster-locked' : ''}${!locked && !readOnly ? ' map-cluster-clickable' : ''}`}
        style={{ cursor: readOnly ? 'default' : (locked ? 'not-allowed' : 'pointer') }}
        onClick={readOnly || locked ? undefined : () => onSelect(cluster)}
      >
        {/* FIX 3: Progress ring uses distinct-concept pct */}
        {!locked && conceptTotal > 0 && (
          <ProgressRing
            cx={0} cy={0}
            pct={conceptPct}
            color={isCleared ? 'var(--success)' : 'var(--accent)'}
          />
        )}

        {/* Main circle */}
        <circle
          cx={0} cy={0}
          r={NODE_R}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          className={isCleared ? 'node-cleared' : touched && !locked ? 'node-active' : ''}
        />

        {/* Cluster ID label */}
        <text
          x={0} y={-6}
          textAnchor="middle"
          fontSize={18}
          fontWeight={800}
          fill={locked ? 'var(--text-muted)' : isCleared ? 'var(--success)' : 'var(--accent-light)'}
          style={{ userSelect: 'none' }}
        >
          {clusterId}
        </text>

        {/* FIX 3: distinct concept count — shows X/Y where Y = distinct within-cluster templates */}
        {conceptTotal > 0 && (
          <text
            x={0} y={10}
            textAnchor="middle"
            fontSize={9}
            fill="var(--text-muted)"
            style={{ userSelect: 'none' }}
          >
            {conceptCleared}/{conceptTotal}
          </text>
        )}

        {/* Full name — wrapped below the circle */}
        {labelLines.map((line, i) => (
          <text
            key={i}
            x={0}
            y={NODE_R + 16 + i * 14}
            textAnchor="middle"
            fontSize={10}
            fill="var(--text-muted)"
            style={{ userSelect: 'none' }}
          >
            {line}
          </text>
        ))}

        {/* Boss badges — top-right of circle */}
        {badges.slice(0, 2).map((badge, i) => (
          <text
            key={badge}
            x={NODE_R - 4}
            y={-NODE_R + 14 + i * 16}
            fontSize={12}
            textAnchor="middle"
            style={{ userSelect: 'none' }}
          >
            {badge}
          </text>
        ))}
      </g>
    </g>
  );
});

/** Break fullName into at most 2 lines of ~16 chars each. */
function splitLabel(name: string): string[] {
  if (name.length <= 16) return [name];
  // Try to break at & or last space before char 17
  const mid = name.indexOf('&');
  if (mid > 0 && mid < name.length - 1) {
    return [name.slice(0, mid + 1).trim(), name.slice(mid + 1).trim()];
  }
  const space = name.lastIndexOf(' ', 16);
  if (space > 0) {
    return [name.slice(0, space), name.slice(space + 1)];
  }
  return [name.slice(0, 16), name.slice(16)];
}

// ── PortalArc (SVG path) ─────────────────────────────────────────────────────

interface PortalArcProps {
  portal: PortalState;
  x1: number; y1: number;
  x2: number; y2: number;
  filterId: string;
  onClick: (portal: PortalState) => void;
  onTooltipEnter: (portal: PortalState, x: number, y: number) => void;
  onTooltipLeave: () => void;
  /** M9-patch11 — hover/tooltip still work; click is a no-op. */
  readOnly?: boolean;
}

// FIX 1: memo so tooltip state changes in the parent don't re-render all portal arcs.
const PortalArc = memo(function PortalArc({
  portal, x1, y1, x2, y2, filterId, onClick, onTooltipEnter, onTooltipLeave, readOnly,
}: PortalArcProps) {
  const { tier, available } = portal;
  const color = tierColor(tier);
  const sw = tierStrokeWidth(tier);
  const dash = tierDashArray(tier);
  const opacity = available ? 1 : 0.25;
  const d = portalArcPath(x1, y1, x2, y2);
  const midX = (x1 + x2) / 2;
  const midY = (y1 + y2) / 2 - Math.min(Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2) * 0.15, 60);

  // FIX 1: stable handlers — call parent with portal object (no inline closure per render)
  const handleClick = useCallback(() => onClick(portal), [onClick, portal]);
  const handleMouseEnter = useCallback(() => onTooltipEnter(portal, midX, midY), [onTooltipEnter, portal, midX, midY]);

  return (
    <g
      className={`portal-arc${available ? ' portal-arc-available' : ''}`}
      opacity={opacity}
      data-testid={`portal-arc-${portal.portalKey}`}
    >
      {/* Glow layer for available portals */}
      {available && (
        <path
          d={d}
          fill="none"
          stroke={color}
          strokeWidth={sw + 6}
          strokeLinecap="round"
          opacity={0.18}
          filter={`url(#${filterId})`}
        />
      )}
      {/* Main arc */}
      <path
        d={d}
        fill="none"
        stroke={color}
        strokeWidth={sw}
        strokeLinecap="round"
        strokeDasharray={dash}
        markerEnd={`url(#portal-arrow-${tier})`}
      />
      {/* Invisible hit area for tooltip/click */}
      <path
        d={d}
        fill="none"
        stroke="transparent"
        strokeWidth={20}
        style={{ cursor: readOnly ? 'default' : 'pointer' }}
        onClick={readOnly ? undefined : handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={onTooltipLeave}
        aria-label={`Portal ${portal.portalKey}`}
        data-testid={`portal-arc-hit-${portal.portalKey}`}
      />
    </g>
  );
});

// ── Main ClusterMap component ─────────────────────────────────────────────────

export function ClusterMap({ mapData, onSelectCluster, onSelectPortal, readOnly }: ClusterMapProps) {
  const { clusters, portals, world2Unlocked } = mapData;
  const [tooltip, setTooltip] = useState<TooltipData | null>(null);
  const [portalModal, setPortalModal] = useState<PortalState | null>(null);
  const uid = useId();
  const glowFilterId = `${uid}-glow`;

  // clusterMap / portalMap reserved for future portal-detail lookups — PORTAL_BATTLE_HOOK

  const handleClusterTooltip = useCallback((cluster: ClusterState, x: number, y: number) => {
    setTooltip({ kind: 'cluster', cluster, x, y });
  }, []);

  const handlePortalClick = useCallback((portal: PortalState) => {
    // PORTAL_BATTLE_HOOK: if available and onSelectPortal is wired, launch the battle.
    // Otherwise fall back to the modal (content-coming for unavailable portals,
    // or modal-with-launch-button when onSelectPortal is provided).
    if (portal.available && onSelectPortal) {
      onSelectPortal(portal);
    } else {
      setPortalModal(portal);
    }
  }, [onSelectPortal]);

  const handlePortalTooltip = useCallback((portal: PortalState, x: number, y: number) => {
    setTooltip({ kind: 'portal', portal, x, y });
  }, []);

  const hideTooltip = useCallback(() => setTooltip(null), []);

  // Collect portal arcs to render: for each portal, find both node positions
  const portalArcs: { portal: PortalState; x1: number; y1: number; x2: number; y2: number }[] = [];
  for (const portal of portals) {
    const parts = portal.portalKey.split('-');
    if (parts.length !== 2) continue;
    const [idA, idB] = parts;
    const posA = LAYOUT[idA];
    const posB = LAYOUT[idB];
    if (!posA || !posB) continue;
    const svgA = toSVG(posA);
    const svgB = toSVG(posB);
    portalArcs.push({ portal, x1: svgA.cx, y1: svgA.cy, x2: svgB.cx, y2: svgB.cy });
  }

  return (
    <div className="map-container" aria-label="EconQuest cluster map">
      {/* World labels */}
      <div className="map-world-labels">
        <span className="map-world-label map-world-label-1">World 1 · Foundation</span>
        <span className={`map-world-label map-world-label-2${world2Unlocked ? '' : ' map-world-dimmed'}`}>
          World 2 · Advanced
        </span>
      </div>

      <svg
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        className="map-svg"
        aria-label="Interactive cluster map"
        role="img"
      >
        <defs>
          {/* Glow filter for available portal arcs */}
          <filter id={glowFilterId} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="6" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>

          {/* Arrow markers for portal arcs */}
          {([6, 9, 12] as const).map((tier) => (
            <marker
              key={tier}
              id={`portal-arrow-${tier}`}
              markerWidth={6} markerHeight={6}
              refX={5} refY={3}
              orient="auto"
            >
              <path
                d="M 0 0 L 6 3 L 0 6 Z"
                fill={
                  tier === 12 ? 'var(--violet)'
                  : tier === 9  ? 'var(--warning)'
                  :               'var(--accent-light)'
                }
              />
            </marker>
          ))}

          {/* Prereq arrow marker */}
          <marker id={`${uid}-prereq-arrow`} markerWidth={6} markerHeight={6} refX={5} refY={3} orient="auto">
            <path d="M 0 0 L 6 3 L 0 6 Z" fill="var(--border)" />
          </marker>
        </defs>

        {/* World region background bands */}
        <rect
          x={0} y={0}
          width={VIEW_W * 0.50}
          height={VIEW_H}
          fill="rgba(99,102,241,0.025)"
          rx={0}
        />
        <rect
          x={VIEW_W * 0.50} y={0}
          width={VIEW_W * 0.50}
          height={VIEW_H}
          fill={world2Unlocked ? 'rgba(139,92,246,0.025)' : 'rgba(0,0,0,0.0)'}
          rx={0}
          opacity={world2Unlocked ? 1 : 0.5}
        />

        {/* Vertical divider */}
        <line
          x1={VIEW_W * 0.50} y1={20}
          x2={VIEW_W * 0.50} y2={VIEW_H - 20}
          stroke="var(--border)"
          strokeWidth={1}
          strokeDasharray="6 8"
          opacity={0.4}
        />

        {/* ── Prereq edges (main journey path) ────────────────────── */}
        <g className="prereq-edges">
          {PREREQ_EDGES.map(([fromId, toId]) => {
            const posA = LAYOUT[fromId];
            const posB = LAYOUT[toId];
            if (!posA || !posB) return null;
            const a = toSVG(posA);
            const b = toSVG(posB);
            const { d, arrowX, arrowY, arrowAngle } = prereqLinePath(a.cx, a.cy, b.cx, b.cy);
            return (
              <g key={`${fromId}-${toId}`}>
                <path
                  d={d}
                  stroke="var(--border)"
                  strokeWidth={1.5}
                  fill="none"
                  opacity={0.5}
                />
                {/* Arrowhead */}
                <polygon
                  points="-5,-3 5,0 -5,3"
                  fill="var(--border)"
                  transform={`translate(${arrowX},${arrowY}) rotate(${arrowAngle})`}
                  opacity={0.5}
                />
              </g>
            );
          })}
        </g>

        {/* ── Portal arcs (behind nodes) ─────────────────────────── */}
        <g className="portal-arcs">
          {portalArcs.map(({ portal, x1, y1, x2, y2 }) => (
            <PortalArc
              key={portal.portalKey}
              portal={portal}
              x1={x1} y1={y1}
              x2={x2} y2={y2}
              filterId={glowFilterId}
              onClick={handlePortalClick}
              onTooltipEnter={handlePortalTooltip}
              onTooltipLeave={hideTooltip}
              readOnly={readOnly}
            />
          ))}
        </g>

        {/* ── Cluster nodes ──────────────────────────────────────── */}
        <g className="cluster-nodes">
          {clusters.map((cluster) => {
            const pos = LAYOUT[cluster.clusterId];
            if (!pos) return null;
            const { cx, cy } = toSVG(pos);
            const dimmed = cluster.world === 2 && !world2Unlocked;
            return (
              <ClusterNode
                key={cluster.clusterId}
                cluster={cluster}
                cx={cx}
                cy={cy}
                dimmed={dimmed}
                onSelect={onSelectCluster}
                onTooltipEnter={handleClusterTooltip}
                onTooltipLeave={hideTooltip}
                readOnly={readOnly}
              />
            );
          })}
        </g>

        {/* ── Tooltip overlay ────────────────────────────────────── */}
        {tooltip && (
          <Tooltip tip={tooltip} svgW={VIEW_W} svgH={VIEW_H} />
        )}
      </svg>

      {/* ── Portal legend ─────────────────────────────────────────── */}
      <div className="map-legend" aria-label="Portal legend">
        <span className="map-legend-title">Portals:</span>
        <span className="map-legend-item map-legend-6m">── 6m path</span>
        <span className="map-legend-item map-legend-9m">╌╌ 9m conditional</span>
        <span className="map-legend-item map-legend-12m">━━ 12m permanent</span>
      </div>

      {/* ── Node state legend ─────────────────────────────────────── */}
      <div className="map-legend" aria-label="Node state legend">
        <span className="map-legend-item">🔒 locked</span>
        <span className="map-legend-item" style={{ color: 'var(--accent-light)' }}>◉ available</span>
        <span className="map-legend-item" style={{ color: 'var(--success)' }}>✓ cleared</span>
      </div>

      {/* ── Portal "content coming soon" modal ───────────────────── */}
      {portalModal && (
        <PortalModal
          portal={portalModal}
          onClose={() => setPortalModal(null)}
          onLaunch={portalModal.available && onSelectPortal ? () => {
            const p = portalModal;
            setPortalModal(null);
            onSelectPortal(p);
          } : undefined}
        />
      )}
    </div>
  );
}


